// ---------------------------------------------------------------------------
// HDDN LAB marketing site — design tokens.
//
// The rest of the app styles itself with inline style objects. The marketing
// site does not, and the reason is functional: inline styles cannot express
// media queries, :hover, :focus-visible or a real dark mode without threading
// JS state through every component, and this site needs all four.
//
// Every selector below is scoped under `.hddn-site`, which the legacy app never
// receives, so none of this can leak into the booking flow or the admin
// dashboard.
//
// Contrast, measured rather than eyeballed (WCAG 2.1 relative luminance):
//   #2b1810 ink on #fdf9f3 paper ............ 15.9:1  AAA
//   #6b5648 muted on #fdf9f3 paper ..........  6.5:1  AA
//   #b8560f accent-ink on #fdf9f3 paper .....  4.5:1  AA  <- orange body text
//   #d97540 accent on #fdf9f3 paper .........  3.0:1  large text / UI only
//   #d97540 accent on #2b1810 dark ..........  5.3:1  AA
//   #b8a495 muted on #2b1810 dark ...........  7.0:1  AA
// Hence the two orange tokens: --accent is the brand orange for display type,
// rules and chrome; --accent-ink is the darker one required whenever orange has
// to carry body-sized text on cream. Never swap them.
// ---------------------------------------------------------------------------

export const MONO = `'Space Mono', 'Courier Prime', ui-monospace, 'Courier New', monospace`;
export const SANS = `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif`;

export const BRAND = {
  ink: '#2b1810',
  accent: '#d97540',
  accentInk: '#b8560f',
  paper: '#fdf9f3',
};

export const siteCss = `
/* ---- tokens: light is the base, defined on bare .hddn-site ---- */
.hddn-site {
  --paper:      #fdf9f3;
  --raised:     #f6efe4;
  --sunk:       #efe5d6;
  --ink:        #2b1810;
  --muted:      #6b5648;
  --accent:     #d97540;
  --accent-ink: #b8560f;
  --on-accent:  #2b1810;
  --line:       #e0d3c2;
  --line-firm:  #c9b8a3;
  --ground:     #2b1810;
  --on-ground:  #fdf9f3;
  --ground-muted: #b8a495;
  --ground-line:  #4a2e1f;
  --ground-raised: #3a2418;
  --focus:      #b8560f;

  --mono: ${MONO};
  --sans: ${SANS};

  --step--1: clamp(11px, 0.68rem + 0.1vw, 12px);
  --step-0:  clamp(15px, 0.9rem + 0.15vw, 17px);
  --step-1:  clamp(18px, 1rem + 0.5vw, 21px);
  --step-2:  clamp(23px, 1.2rem + 1.1vw, 30px);
  --step-3:  clamp(30px, 1.4rem + 2.2vw, 46px);
  --step-4:  clamp(38px, 1.6rem + 4.2vw, 76px);
  --step-5:  clamp(46px, 1.8rem + 6.4vw, 104px);

  --gap:   clamp(16px, 4vw, 28px);
  --bay:   clamp(48px, 9vw, 104px);
  --shell: 1180px;

  color-scheme: light;
  background: var(--paper);
  color: var(--ink);
  font-family: var(--sans);
  font-size: var(--step-0);
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
}

/* ---- dark: system preference, unless an explicit light choice overrides ---- */
@media (prefers-color-scheme: dark) {
  .hddn-site:not([data-theme="light"]) {
    --paper:      #1c0f09;
    --raised:     #2b1810;
    --sunk:       #150b06;
    --ink:        #fdf9f3;
    --muted:      #b8a495;
    --accent:     #e8874f;
    --accent-ink: #e8874f;
    --on-accent:  #1c0f09;
    --line:       #43291c;
    --line-firm:  #6b4530;
    --ground:     #150b06;
    --on-ground:  #fdf9f3;
    --ground-muted: #b8a495;
    --ground-line:  #43291c;
    --ground-raised: #2b1810;
    --focus:      #e8874f;
    color-scheme: dark;
  }
}

/* ---- dark: explicit choice, wins in both directions ---- */
.hddn-site[data-theme="dark"] {
  --paper:      #1c0f09;
  --raised:     #2b1810;
  --sunk:       #150b06;
  --ink:        #fdf9f3;
  --muted:      #b8a495;
  --accent:     #e8874f;
  --accent-ink: #e8874f;
  --on-accent:  #1c0f09;
  --line:       #43291c;
  --line-firm:  #6b4530;
  --ground:     #150b06;
  --on-ground:  #fdf9f3;
  --ground-muted: #b8a495;
  --ground-line:  #43291c;
  --ground-raised: #2b1810;
  --focus:      #e8874f;
  color-scheme: dark;
}

/* ---- reset, scoped ---- */
.hddn-site *, .hddn-site *::before, .hddn-site *::after { box-sizing: border-box; }
.hddn-site img, .hddn-site video { max-width: 100%; display: block; }
.hddn-site h1, .hddn-site h2, .hddn-site h3, .hddn-site h4, .hddn-site p,
.hddn-site figure, .hddn-site ul, .hddn-site ol { margin: 0; }
.hddn-site ul, .hddn-site ol { padding: 0; list-style: none; }
/* :where() keeps this at .hddn-site's specificity instead of .hddn-site+a, so a
   component class on a link — a button, a nav item, the fork doors — still wins.
   Without it the reset silently outranked every one of them and links took the
   inherited colour of whatever band they sat in. */
.hddn-site :where(a) { color: inherit; text-decoration: none; }
/* Same reason as the link reset above: :where() keeps this at .hddn-site's own
   specificity, so a button that also carries .hddn-btn--fill keeps its fill.
   Without it every form submit rendered as bare text. */
.hddn-site :where(button) { font: inherit; color: inherit; background: none; border: 0; cursor: pointer; }

.hddn-site :focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 3px;
  border-radius: 1px;
}

.hddn-skip {
  position: absolute; left: -9999px; top: 0; z-index: 100;
  background: var(--ink); color: var(--paper);
  padding: 12px 18px; font-family: var(--mono); font-size: var(--step--1);
  letter-spacing: 0.14em; text-transform: uppercase;
}
.hddn-skip:focus { left: 8px; top: 8px; }

/* ---- layout ---- */
.hddn-shell { width: 100%; max-width: var(--shell); margin: 0 auto; padding: 0 var(--gap); }
.hddn-shell--narrow { max-width: 780px; }
.hddn-bay { padding: var(--bay) 0; }
.hddn-bay--tight { padding: calc(var(--bay) * 0.55) 0; }
/* Two plain sections in a row would stack two full gaps, so the second drops
   its top padding. A section that starts a new band, or follows one, keeps it:
   content sitting flush against a colour change reads as cramped, which is
   exactly what the first pass shipped. */
.hddn-bay + .hddn-bay { padding-top: 0; }
.hddn-bay + .hddn-bay[class*="hddn-band--"],
.hddn-bay[class*="hddn-band--"] + .hddn-bay { padding-top: var(--bay); }
.hddn-bay + .hddn-bay--tight[class*="hddn-band--"],
.hddn-bay[class*="hddn-band--"] + .hddn-bay--tight { padding-top: calc(var(--bay) * 0.55); }
.hddn-rule { border: 0; border-top: 1px solid var(--line); margin: 0; }

.hddn-band--raised { background: var(--raised); }
.hddn-band--sunk   { background: var(--sunk); }
.hddn-band--ground { background: var(--ground); color: var(--on-ground); }
.hddn-band--ground .hddn-eyebrow { color: var(--accent); }
.hddn-band--ground .hddn-lede,
.hddn-band--ground .hddn-muted { color: var(--ground-muted); }
.hddn-band--ground .hddn-rule,
.hddn-band--ground .hddn-card { border-color: var(--ground-line); }

/* ---- type ---- */
.hddn-eyebrow {
  font-family: var(--mono); font-size: var(--step--1); font-weight: 700;
  letter-spacing: 0.2em; text-transform: uppercase; color: var(--accent-ink);
  display: flex; align-items: center; gap: 10px;
}
.hddn-eyebrow::before {
  content: ''; width: 22px; height: 2px; background: var(--accent); flex: none;
}
.hddn-eyebrow--plain::before { display: none; }

.hddn-h1 { font-size: var(--step-4); line-height: 0.98; letter-spacing: -0.03em; font-weight: 800; }
.hddn-h2 { font-size: var(--step-3); line-height: 1.04; letter-spacing: -0.025em; font-weight: 800; }
.hddn-h3 { font-size: var(--step-2); line-height: 1.12; letter-spacing: -0.02em; font-weight: 700; }
.hddn-h4 { font-size: var(--step-1); line-height: 1.25; letter-spacing: -0.01em; font-weight: 700; }

.hddn-lede { font-size: var(--step-1); line-height: 1.5; color: var(--muted); }
.hddn-muted { color: var(--muted); }
.hddn-prose p + p { margin-top: 1em; }
.hddn-prose strong { font-weight: 700; color: var(--ink); }

.hddn-label {
  font-family: var(--mono); font-size: var(--step--1); font-weight: 700;
  letter-spacing: 0.16em; text-transform: uppercase; color: var(--muted);
}

/* ---- the number motif: large tabular mono figures ---- */
.hddn-figs { display: grid; gap: 1px; background: var(--line); border: 1px solid var(--line); }
.hddn-figs--4 { grid-template-columns: repeat(2, 1fr); }
.hddn-figs--3 { grid-template-columns: 1fr; }
@media (min-width: 720px) {
  .hddn-figs--4 { grid-template-columns: repeat(4, 1fr); }
  .hddn-figs--3 { grid-template-columns: repeat(3, 1fr); }
}
.hddn-fig { background: var(--paper); padding: clamp(18px, 3vw, 28px); }
.hddn-band--raised .hddn-fig { background: var(--raised); }
.hddn-band--ground .hddn-fig { background: var(--ground); }
.hddn-band--ground .hddn-figs { background: var(--ground-line); border-color: var(--ground-line); }
.hddn-fig__n {
  font-family: var(--mono); font-weight: 700; font-size: var(--step-3);
  font-variant-numeric: tabular-nums; letter-spacing: -0.03em; line-height: 1;
  display: block; color: var(--ink);
}
.hddn-band--ground .hddn-fig__n { color: var(--on-ground); }
.hddn-fig__n em { font-style: normal; color: var(--accent); }
.hddn-fig__k {
  display: block; margin-top: 10px; font-family: var(--mono);
  font-size: var(--step--1); letter-spacing: 0.14em; text-transform: uppercase;
  color: var(--muted);
}
.hddn-band--ground .hddn-fig__k { color: var(--ground-muted); }
.hddn-fig__note { display: block; margin-top: 6px; font-size: var(--step--1); color: var(--muted); }

/* ---- buttons ---- */
.hddn-btn {
  display: inline-flex; align-items: center; gap: 10px; justify-content: center;
  font-family: var(--mono); font-size: var(--step--1); font-weight: 700;
  letter-spacing: 0.14em; text-transform: uppercase;
  padding: 15px 24px; min-height: 48px; border: 1px solid transparent;
  transition: background .15s, color .15s, border-color .15s, transform .15s;
}
.hddn-btn:hover { transform: translateY(-1px); }
.hddn-btn--fill { background: var(--accent); color: var(--on-accent); }
.hddn-btn--fill:hover { background: var(--accent-ink); color: var(--paper); }
.hddn-site[data-theme="dark"] .hddn-btn--fill:hover,
.hddn-band--ground .hddn-btn--fill:hover { background: var(--paper); color: var(--ground); }
.hddn-btn--line { border-color: var(--line-firm); color: var(--ink); }
.hddn-btn--line:hover { border-color: var(--ink); background: var(--ink); color: var(--paper); }
.hddn-band--ground .hddn-btn--line { border-color: var(--ground-line); color: var(--on-ground); }
.hddn-band--ground .hddn-btn--line:hover { background: var(--on-ground); color: var(--ground); }
.hddn-btn--wide { width: 100%; }

/* ---- cards ---- */
.hddn-card { border: 1px solid var(--line); background: var(--paper); padding: clamp(20px, 3vw, 32px); }
.hddn-band--raised .hddn-card { background: var(--paper); }
.hddn-band--ground .hddn-card { background: var(--ground-raised); color: var(--on-ground); }

.hddn-grid { display: grid; gap: var(--gap); }
@media (min-width: 640px) { .hddn-grid--2 { grid-template-columns: repeat(2, 1fr); } }
@media (min-width: 900px) {
  .hddn-grid--3 { grid-template-columns: repeat(3, 1fr); }
  .hddn-grid--sidebar { grid-template-columns: 1.25fr 1fr; align-items: start; }
}

/* ---- the fork: the one component the whole site is built around ---- */
.hddn-fork { display: grid; gap: 1px; background: var(--line-firm); border: 1px solid var(--line-firm); }
@media (min-width: 780px) { .hddn-fork { grid-template-columns: 1fr 1fr; } }
@media (min-width: 900px) { .hddn-fork--3 { grid-template-columns: repeat(3, 1fr); } }
.hddn-door {
  display: flex; flex-direction: column; gap: 14px; align-items: flex-start;
  background: var(--paper); color: var(--ink); padding: clamp(24px, 4vw, 44px);
  transition: background .18s, color .18s;
}
.hddn-door:hover, .hddn-door:focus-visible { background: var(--ink); color: var(--paper); }
.hddn-door:hover .hddn-door__k, .hddn-door:focus-visible .hddn-door__k { color: var(--accent); }
.hddn-door:hover .hddn-door__p, .hddn-door:focus-visible .hddn-door__p { color: var(--paper); }
.hddn-door:hover .hddn-door__go, .hddn-door:focus-visible .hddn-door__go { color: var(--accent); }
.hddn-door__k {
  font-family: var(--mono); font-size: var(--step--1); font-weight: 700;
  letter-spacing: 0.2em; text-transform: uppercase; color: var(--accent-ink);
}
.hddn-door__h { font-size: var(--step-2); line-height: 1.05; letter-spacing: -0.02em; font-weight: 800; }
.hddn-door__p { color: var(--muted); font-size: var(--step-0); }
.hddn-door__go {
  margin-top: auto; padding-top: 12px; font-family: var(--mono); font-weight: 700;
  font-size: var(--step--1); letter-spacing: 0.14em; text-transform: uppercase;
  color: var(--accent-ink);
}

/* ---- rate tables ---- */
.hddn-rates { width: 100%; border-collapse: collapse; }
.hddn-rates th, .hddn-rates td { text-align: left; padding: 14px 0; border-bottom: 1px solid var(--line); }
.hddn-rates th {
  font-family: var(--mono); font-size: var(--step--1); font-weight: 700;
  letter-spacing: 0.14em; text-transform: uppercase; color: var(--muted);
}
.hddn-rates td:last-child, .hddn-rates th:last-child { text-align: right; }
.hddn-rates .hddn-price {
  font-family: var(--mono); font-weight: 700; font-variant-numeric: tabular-nums;
  font-size: var(--step-1); color: var(--ink); white-space: nowrap;
}
.hddn-rates .hddn-sub { display: block; font-size: var(--step--1); color: var(--muted); margin-top: 3px; }

/* ---- media ---- */
.hddn-frame { position: relative; overflow: hidden; background: var(--sunk); border: 1px solid var(--line); }
.hddn-frame > img { width: 100%; height: 100%; object-fit: cover; }
.hddn-frame--wide  { aspect-ratio: 16 / 9; }
/* On a phone a full 16:9 hero pushes the fork — the one thing a cold visitor
   actually needs — well below the fold. Crop it instead. */
.hddn-frame--hero { aspect-ratio: 16 / 9; }
@media (max-width: 700px) { .hddn-frame--hero { aspect-ratio: 3 / 2; max-height: 40vh; } }
.hddn-frame--tall  { aspect-ratio: 4 / 5; }
.hddn-frame--square{ aspect-ratio: 1 / 1; }
.hddn-frame__play {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
}
.hddn-frame__play span {
  width: 62px; height: 62px; border-radius: 50%; background: var(--accent); color: var(--on-accent);
  display: grid; place-items: center; font-size: 20px; padding-left: 4px;
}
.hddn-strip { display: grid; grid-auto-flow: column; grid-auto-columns: 78%; gap: 12px; overflow-x: auto; scroll-snap-type: x mandatory; padding-bottom: 4px; }
.hddn-strip > * { scroll-snap-align: start; }
.hddn-strip::-webkit-scrollbar { height: 6px; }
.hddn-strip::-webkit-scrollbar-thumb { background: var(--line-firm); }
@media (min-width: 640px) { .hddn-strip { grid-auto-columns: 42%; } }
@media (min-width: 1000px) { .hddn-strip { grid-auto-columns: 30%; } }

/* ---- collections: one card per folder of work ---- */
.hddn-folders { display: grid; gap: var(--gap); }
@media (min-width: 640px)  { .hddn-folders { grid-template-columns: repeat(2, 1fr); } }
@media (min-width: 1000px) { .hddn-folders { grid-template-columns: repeat(3, 1fr); } }
.hddn-folder { display: block; color: inherit; text-align: left; width: 100%; padding: 0; }
.hddn-folder .hddn-frame { transition: border-color .18s; }
.hddn-folder:hover .hddn-frame, .hddn-folder:focus-visible .hddn-frame { border-color: var(--accent); }
.hddn-folder__t { margin-top: 12px; font-weight: 700; font-size: var(--step-1); letter-spacing: -0.01em; }
.hddn-folder__n { margin-top: 4px; font-family: var(--mono); font-size: var(--step--1); letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted); }

/* ---- chips ---- */
.hddn-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.hddn-chip {
  font-family: var(--mono); font-size: var(--step--1); font-weight: 700;
  letter-spacing: 0.1em; text-transform: uppercase;
  padding: 9px 14px; min-height: 40px; border: 1px solid var(--line-firm); color: var(--muted);
}
.hddn-chip[aria-pressed="true"] { background: var(--ink); border-color: var(--ink); color: var(--paper); }

/* ---- lists ---- */
.hddn-ticks li { position: relative; padding-left: 22px; margin-bottom: 9px; }
.hddn-ticks li::before {
  content: ''; position: absolute; left: 0; top: 0.62em;
  width: 9px; height: 2px; background: var(--accent);
}
.hddn-defs { display: grid; gap: 2px; }
.hddn-def { padding: 14px 0; border-bottom: 1px solid var(--line); }
.hddn-def dt { font-family: var(--mono); font-size: var(--step--1); font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: var(--muted); margin-bottom: 5px; }

/* ---- nav ---- */
.hddn-nav { position: sticky; top: 0; z-index: 40; background: var(--ground); color: var(--on-ground); border-bottom: 2px solid var(--accent); }
.hddn-nav__in { display: flex; align-items: center; justify-content: space-between; gap: 16px; min-height: 62px; }
.hddn-mark { display: flex; align-items: baseline; gap: 8px; font-family: var(--sans); font-weight: 800; letter-spacing: 0.16em; font-size: 17px; text-transform: uppercase; }
.hddn-mark b { font-weight: 800; }
.hddn-mark i { font-style: normal; font-family: var(--mono); font-size: 10px; letter-spacing: 0.22em; color: var(--accent); }
.hddn-nav__links { display: none; gap: 2px; align-items: center; }
.hddn-nav__burger { display: inline-flex; align-items: center; gap: 9px; font-family: var(--mono); font-size: var(--step--1); font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; padding: 12px; min-height: 48px; }
@media (min-width: 940px) {
  .hddn-nav__links { display: flex; }
  .hddn-nav__burger { display: none; }
}
.hddn-nav__a {
  font-family: var(--mono); font-size: var(--step--1); font-weight: 700;
  letter-spacing: 0.14em; text-transform: uppercase; padding: 10px 12px; color: var(--ground-muted);
}
.hddn-nav__a:hover { color: var(--on-ground); }
.hddn-nav__a[aria-current="page"] { color: var(--accent); }
.hddn-drawer { border-top: 1px solid var(--ground-line); padding: 8px 0 18px; }
.hddn-drawer .hddn-nav__a { display: block; padding: 14px 0; font-size: var(--step-0); letter-spacing: 0.1em; }
@media (min-width: 940px) { .hddn-drawer { display: none; } }

/* ---- footer ---- */
.hddn-foot { background: var(--ground); color: var(--on-ground); padding: var(--bay) 0 32px; }
.hddn-foot a:hover { color: var(--accent); }
.hddn-foot__cols { display: grid; gap: var(--gap); }
@media (min-width: 760px) { .hddn-foot__cols { grid-template-columns: 1.6fr 1fr 1fr; } }
.hddn-foot__base { margin-top: 44px; padding-top: 22px; border-top: 1px solid var(--ground-line); display: flex; flex-wrap: wrap; gap: 14px; justify-content: space-between; align-items: center; font-family: var(--mono); font-size: var(--step--1); color: var(--ground-muted); letter-spacing: 0.08em; }

/* ---- forms ---- */
.hddn-field { display: block; margin-bottom: 18px; }
.hddn-field > span { display: block; font-family: var(--mono); font-size: var(--step--1); font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: var(--muted); margin-bottom: 7px; }
.hddn-input, .hddn-site textarea.hddn-input, .hddn-site select.hddn-input {
  width: 100%; font: inherit; font-size: var(--step-0); color: var(--ink);
  background: var(--paper); border: 1px solid var(--line-firm);
  padding: 13px 14px; min-height: 48px; border-radius: 0;
}
.hddn-input:focus { border-color: var(--accent-ink); }
.hddn-site textarea.hddn-input { min-height: 140px; resize: vertical; }
.hddn-note { font-size: var(--step--1); color: var(--muted); }
.hddn-alert { border-left: 3px solid var(--accent); background: var(--raised); padding: 14px 16px; font-size: var(--step-0); }

/* ---- misc ---- */
.hddn-vis-hidden { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
@media (prefers-reduced-motion: reduce) {
  .hddn-site *, .hddn-site *::before, .hddn-site *::after {
    animation-duration: .01ms !important; transition-duration: .01ms !important;
  }
  .hddn-btn:hover { transform: none; }
}
`;
